import { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { toggleProjectSidebar } from "../../../../actions/projectSidebar";
import { updateMyTasksAction, updateProjectTaskAction } from "../../../../actions/task";
import { getUserDetails, updateProfile } from "../../../../actions/userProfile";
import { addAdminVariableUserProfileValue } from "../../../../components/Admin/admin.service";
import { PROJECT_TYPE } from "../../../../components/ProjectSidebar/projectSidebar.constants";
import { appendFieldsFromObject, createFormData } from "../../../../helper/formDataHelper";
import { UserProfileContext } from "./UserProfile.context";
import "./userProfile.scss";

const useUserProfile = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state?.auth);
  const { projectId } = useParams();
  const { allTasks } = useSelector((state) => state.tasks);
  const { profileData, timeZone, setIsEnableSaveButton, updatedVariables, setUpdatedVariables } = useContext(UserProfileContext);
  const { visitingByAdmin, details } = useSelector((state) => state.userDetails);

  const saveProfileData = async (e) => {
    e.preventDefault();
    let newProfileData;
    if (profileData.Manager === null) {
      const { Manager, ...updatedProfileData } = profileData;
      newProfileData = updatedProfileData;
    } else newProfileData = profileData;
    if (timeZone ) {
      newProfileData = { ...newProfileData, timezone: timeZone };
    }
    const response = await updateProfileSetup(newProfileData);
    if (response) {
      const responseUser = await dispatch(getUserDetails(user?.id));

      if (responseUser) {
        if (projectId) {
          const updatedProjectTasks = updateUserPhotoInTasks(allTasks?.projectTasks, responseUser);
          dispatch(updateProjectTaskAction(updatedProjectTasks));
        } else {
          const updatedMyTasks = updateUserPhotoInTasks(allTasks?.myTasks, responseUser);
          dispatch(updateMyTasksAction(updatedMyTasks));
        }
        setIsEnableSaveButton(false);
      }
    }
  };

  /**
   * Saves the updated variables by calling the updateProfileSetup api.
   * @author Sarthak Arora
   */
  const saveUpdatedVariables = async () => {
    const response = await updateProfileSetup(profileData);
    if (response?.affectedRows) {
       await addAdminVariableUserProfileValue(updatedVariables);
      if (setUpdatedVariables) {
        setUpdatedVariables([]);
      }
    }
    setIsEnableSaveButton(false);
  };

  /**
   * Updates user photo in tasks (projectTasks or myTasks) based on user id.
   * @param {Array} tasks - Array of tasks (projectTasks or myTasks)
   * @param {Object} user - User object containing id and PhotoLink
   * @returns {Array} - Updated tasks array
   * @author Sejal
   */
  const updateUserPhotoInTasks = (tasks, user) => {
    if (!tasks || !user) return [];
    return tasks.map((task) => {
      let updatedTask = { ...task };
      if (Number(task.Assignee) === Number(user?.id)) {
        updatedTask.AssigneePhotos = user.PhotoLinks;
      }
      if (Number(task.AssignedById) === Number(user?.id)) {
        updatedTask.AssignedByPhotos = user?.PhotoLinks;
      }
      return updatedTask;
    });
  };

  /**
   * Hides the project sidebar by dispatching the toggleProjectSidebar action with the PERSONAL_PROJECT type.
   * @author Bhavana
   */
  const hideProjectSidebar = () => {
    dispatch(toggleProjectSidebar(PROJECT_TYPE.PERSONAL_PROJECT, false));
  };

  /**
   * Updates the user profile setup by creating a payload and call updateProfile api.
   * @author Bhavana
   */
  const updateProfileSetup = async (profileData) => {
    const payloadProfile = {
      ...profileData,
      userId: visitingByAdmin ? details?.id : user?.id,
    };
    const formData = createFormData();
    appendFieldsFromObject(formData, payloadProfile);
    const response = updateProfile(formData);
    return response;
  };

  return {
    saveProfileData,
    updateUserPhotoInTasks,
    hideProjectSidebar,
    updateProfileSetup,
    saveUpdatedVariables,
  };
};

export default useUserProfile;
